<template>
  <div class="site-header-container">
    <header class="site-header">
      <div class="site-header__container container">
        <!-- Left -->
        <div class="site-header__left">
          <button
            v-if="mobile.handle"
            class="site-header__menu icon-button"
            type="button"
            @click.prevent="handleMenuToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.menu')"
            />

            <icon-menu />
          </button>

          <button
            class="site-header__search icon-button"
            type="button"
            @click.prevent="openSearchOverlay"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.search.title')"
            />

            <icon-search />
          </button>

          <!-- Logo - desktop -->
          <component
            :is="logoElement"
            v-show="!isMobile"
            class="site-header__logo"
          >
            <a :href="$variable('routes.root')">
              <!-- eslint-disable-next-line -->
              <img
                :alt="logo.alt || $variable('store.shopName')"
                height="48"
                :src="$formatImageUrl(logo.url, { width: 192 })"
                :srcset="getLogoSrcset(48)"
                sizes="48px"
                width="48"
              >
            </a>
          </component>

          <site-header-garage
            class="site-header__desktop-garage site-header__buttons"
            template="desktop"
          />
        </div>

        <!-- Logo - mobile -->
        <div class="site-header__center">
          <component
            :is="logoElement"
            v-show="isMobile"
            class="site-header__logo"
          >
            <a :href="$variable('routes.root')">
              <!-- eslint-disable-next-line -->
              <img
                :alt="logo.alt || $variable('store.shopName')"
                height="42"
                :src="$formatImageUrl(logo.url, { width: 168 })"
                :srcset="getLogoSrcset(42)"
                sizes="42px"
                width="42"
              >
            </a>
          </component>

          <search-bar
            ref="searchBar"
            class="site-header__search-bar"
            :namespace="liquid.sectionId"
            redirect
            :search-query="searchQuery"
            @search-input="(input) => searchQuery = input"
            @search-click="openSearchOverlay"
            @clear="clearSearch"
          />
        </div>

        <!-- Right -->
        <div class="site-header__right">
          <div class="site-header__buttons account">
            <a
              aria-hidden="true"
              :href="$variable('routes.account.url')"
              tabindex="-1"
            >
              <span
                class="visually-hidden"
                v-text="$string('customers.header.my_account')"
              />

              <icon-account />
            </a>

            <ul class="site-header__buttons--header text-body-s-regular">
              <li>
                <a
                  class="text-body-s-bold"
                  :href="$variable('routes.account.url')"
                  v-text="$string('customers.header.my_account')"
                />
              </li>

              <li v-if="!isLoggedIn">
                <a
                  class="text-body-s-regular"
                  :href="$variable('routes.account.login')"
                  v-text="$string('customers.header.sign_in')"
                />
              </li>
            </ul>
          </div>

          <a
            class="site-header__buttons cart"
            :href="$variable('routes.cart.url')"
            @click="handleCartToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.cart')"
            />
            <icon-cart-empty
              v-if="cart.item_count === 0"
            />

            <icon-cart
              v-else
            />

            <span class="site-header__buttons--header">
              <p
                class="text-body-s-bold"
                v-text="$string('general.site_header.basket')"
              />

              <p
                class="text-body-s-regular"
                v-text="$formatMoney(cart.total_price)"
              />
            </span>
          </a>

          <button
            class="site-header__buttons country text-start"
            :aria-expanded="activeOverlayCountrySwitcher"
            aria-haspopup="dialog"
            type="button"
            @click="handleCountySwitcher"
          >
            <!-- eslint-disable-next-line -->
            <img
              v-if="countryFlag"
              :key="`flag-${$variable('store.country')}`"
              class="site-header__country-flag"
              alt=""
              height="24"
              role="presentation"
              :src="countryFlag"
              width="24"
            >

            <span class="site-header__buttons--header">
              <p
                class="text-body-s-bold"
                v-text="$string('general.site_header.country')"
              />

              <p
                class="text-body-s-regular"
                v-text="$string(
                  'general.multi_country.country_currency_code_symbol',
                  { replace: replaceStrings() },
                )"
              />
            </span>
          </button>
        </div>
      </div>

      <site-header-garage
        class="site-header__mobile-garage"
        template="mobile"
      />
    </header>

    <!-- Search -->
    <overlay
      key="predictiveSearch"
      block-class="predictive-search-overlay"
      :direction="isMobile ? 'right' : 'down'"
      namespace="predictiveSearch"
      :template="false"
      type="drawer-shelf"
    >
      <template #body="overlay">
        <predictive-search
          :header-search-bar="$refs.searchBar ? $refs.searchBar.$el : null"
          :namespace="liquid.sectionId"
          :overlay="overlay"
          :search-query="searchQuery"
          @clear="clearSearch"
          @search-input="(input) => searchQuery = input"
        />
      </template>
    </overlay>

    <!-- Menu drawer -->
    <teleport
      v-if="mobile.handle"
      to="body"
    >
      <overlay
        key="menuDrawer"
        block-class="menu-drawer-overlay"
        direction="right"
        namespace="menuDrawer"
        type="drawer"
      >
        <template #body="overlay">
          <menu-drawer
            :handle="mobile.handle"
            :item-count="mobile.itemCount"
            :overlay="overlay"
            :parent-collection-url="parentCollectionUrl"
          />
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} logo - Formatted logo image object.
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {String} [parentCollectionUrl] - Parent collection of current product.
 *
 */
// eslint-disable-next-line import/named
import { mapActions, mapGetters, mapState } from 'vuex'

import MenuDrawer from '~async/menu-drawer/menu-drawer'
import Overlay from '~global/overlay/overlay'
import PredictiveSearch from '~async/predictive-search/predictive-search'
import SearchBar from '~global/search-bar/search-bar'
import SiteHeaderGarage from '~global/site-header-garage/site-header-garage'

import IconAccount from '~icons/general/account.svg'
import IconCart from '~icons/general/cart.svg'
import IconCartEmpty from '~icons/general/cart-empty.svg'
import IconMenu from '~icons/general/menu.svg'
import IconSearch from '~icons/general/search.svg'

export default {
  name: 'SiteHeader',

  components: {
    IconAccount,
    IconCart,
    IconCartEmpty,
    IconMenu,
    IconSearch,
    MenuDrawer,
    Overlay,
    PredictiveSearch,
    SearchBar,
    SiteHeaderGarage,
  },

  props: {
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    logo: {
      type: Object,
      default: () => ({}),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 5,
      }),
    },
    parentCollectionUrl: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isLoggedIn: cnvs.customer.loggedIn,
      searchQuery: '',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if multi-country switcher overlay is active.
     * @returns {Boolean}
     */
    activeOverlayCountrySwitcher() {
      return this.getActiveOverlay?.namespace === 'multiCountrySwitcher'
    },

    /**
     * Compute logo element.
     * @returns {String}
     */
    logoElement() {
      return this.$variable('request.path') === '/' ? 'h1' : 'span'
    },

    /**
     * Compute flag URL.
     * @returns {String}
     */
    countryFlag() {
      const code = this.$variable('store.country')?.toLowerCase()

      if (!code) {
        return ''
      }

      return `https://cdn.shopify.com/static/images/flags/${code}.svg`
    },
  },

  watch: {

    /**
     * Watch active overlay to focus search bar.
     */
    async getActiveOverlay() {
      if (
        this.getActiveOverlay?.namespace !== 'predictiveSearch' ||
        this.isMobile
      ) {
        return
      }

      // Focus search bar input after overlay opened.
      await this.$nextTick()
      setTimeout(() => {
        this.$refs.searchBar.$refs.input.focus()
      }, this.$timing('slow'))
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Get logo srcset sizes.
     * @param {Number} size - Starting size.
     * @returns {String}
     */
    getLogoSrcset(size) {
      return `
        ${this.$formatImageUrl(this.logo.url, { width: size })} ${size}w,
        ${this.$formatImageUrl(this.logo.url, { width: size * 2 })} ${size * 2}w,
        ${this.$formatImageUrl(this.logo.url, { width: size * 3 })} ${size * 3}w,
        ${this.$formatImageUrl(this.logo.url, { width: size * 4 })} ${size * 4}w
      `
    },

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    /**
     * Open predictive search overlay on search submit.
     */
    openSearchOverlay() {
      if (this.getActiveOverlay?.namespace === 'predictiveSearch') {
        return
      }

      this.openOverlay({
        component: 'predictive-search',
        ignoreDismissed: true,
        namespace: 'predictiveSearch',
      })
    },

    /**
     * Clear search query.
     */
    clearSearch() {
      this.searchQuery = ''
    },

    /**
     * Handle country switcher.
     */
    handleCountySwitcher() {
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'multiCountrySwitcher',
      })
    },

    /**
     * Replace text shortcodes.
     * @returns {Object}
     */
    replaceStrings() {
      return {
        country_name: cnvs.store.countryName,
        currency_code: cnvs.store.currency,
        currency_symbol: cnvs.store.currencySymbol,
      }
    },
  },
}
</script>
